import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  formatData,
  formatDatasetPath,
  handleError,
  Role,
  Asset,
  CUSTOM_PROPERTY_OBJECT_MAP,
  resolveGlossaryAssetSubmit,
} from '../helpers';
import {
  CheckboxList,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  Tabs,
} from '../components';
import {
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  useNotFound,
  useMessage,
} from '../hooks';
import {
  customPropertyService,
  customPropertyObjectMapService,
  customPropertyValueService,
  customPropertyListValueService,
  entityService,
  datasetService,
  datasetGroupService,
  datasourceService,
  fieldService,
  attributeService,
  attributeValueService,
  glossaryService,
  systemService,
} from '../services';

export const CustomProperty = () => {
  const { queryParams, resetParams } = useQueryParams();

  const [customProperty, setCustomProperty] = useState();
  const [customPropertyObjectMap, setCustomPropertyObjectMap] = useState([]);
  const [customPropertyListValues, setCustomPropertyListValues] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [assignedAssetsCount, setAssignedAssetsCount] = useState({});
  const [assignedAssetsTotalCount, setAssignedAssetsTotalCount] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [defaultSelectedObject, setDefaultSelectedObject] = useState({ tab: queryParams.object });
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { custom_property_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { notFound, setNotFound, NotFound } = useNotFound();

  useEffect(() => {
    fetchItems();
    fetchCustomPropertyObjectMap();
    fetchAssignedAssetsCount();
  }, [custom_property_id]);

  useEffect(() => {
    customProperty && fetchCustomPropertyListValues();
  }, [customProperty]);

  const fetchItems = async () => {
    customPropertyService
      .getById(custom_property_id, true)
      .then(res => {
        if (res.custom_properties.length < 1) {
          setNotFound(true);
        } else {
          setCustomProperty(res.custom_properties[0]);
          setLoading(false);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  const fetchCustomPropertyObjectMap = async () => {
    const customPropertyIds = [custom_property_id];

    if (customPropertyIds.length > 0) {
      customPropertyObjectMapService
        .getByCustomPropertyId(customPropertyIds)
        .then(res => {
          if (res) {
            let objectMap = res.custom_property_object_map.map(x => ({ value: x.object_name }));
            setCustomPropertyObjectMap(objectMap);
          }
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    }
  };

  const fetchAssignedAssetsCount = async () => {
    customPropertyService
      .getAssignedAssetsCountByObject(custom_property_id)
      .then(res => {
        let tmp = {};
        let defaultObject;

        const objectSortOrder = [
          'attribute_value',
          'attribute',
          'entity',
          'glossary',
          'field',
          'dataset',
          'dataset_group',
          'datasource',
          'system',
        ];
        res.assigned_assets_count.sort(
          (a, b) => objectSortOrder.indexOf(a.object_name) - objectSortOrder.indexOf(b.object_name)
        );

        res.assigned_assets_count.forEach(result => {
          if (result.custom_property_value_count > 0 && defaultObject === undefined) {
            defaultObject = result.object_name;
          }
          tmp = { ...tmp, [result.object_name]: parseInt(result.custom_property_value_count) };
        });

        defaultObject = res.assigned_assets_count.find(x => x.object_name === queryParams.object)
          ? queryParams.object
          : defaultObject;

        setDefaultSelectedObject({ tab: defaultObject });

        fetchAssignedAssets(defaultObject);
        setAssignedAssetsCount(tmp);

        setAssignedAssetsTotalCount(
          (tmp.attribute_value || 0) +
            (tmp.attribute || 0) +
            (tmp.field || 0) +
            (tmp.dataset || 0) +
            (tmp.dataset_group || 0) +
            (tmp.datasource || 0) +
            (tmp.system || 0) +
            (tmp.entity || 0) +
            (tmp.glossary || 0)
        );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAssignedAssets = async objectName => {
    try {
      if (objectName) {
        // objectName is undefined if user does not own any items

        setLoadingTable(true);

        // Fetch assets
        let promises = [
          customPropertyService.getAssignedAssets(custom_property_id, objectName).catch(err => {
            throw err;
          }),
        ];

        const res = await Promise.all(promises).catch(err => {
          throw err;
        });

        let result = res[0].assigned_assets;

        if (objectName === Asset.Dataset || objectName === Asset.Field) {
          // Render the (field's) dataset as its full endpoint path (API datasets); the ancestors
          // are attached to the row by the assigned-assets endpoint.
          result = result.map(x => ({
            ...x,
            dataset_path: formatDatasetPath(x, { ancestors: x.ancestors }),
          }));
        }

        setAssignedAssets(result);
      }
    } catch (err) {
      showMessage(err, 'error');
    } finally {
      setLoadingTable(false); // Needed to show "no result" message
    }
  };

  const fetchCustomPropertyListValues = async () => {
    if (customProperty?.custom_property_type === 'list') return; // Only applicable to custom properties of type "list"

    customPropertyListValueService
      .getByCustomPropertyId(custom_property_id)
      .then(res => {
        res && setCustomPropertyListValues(res.custom_property_list_values);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /*
    const fetchItems = async () => {
    customPropertyService
      .getById(custom_property_id, true)
      .then(res => {
        if (res.custom_properties.length < 1) {
          setNotFound(true);
        } else {
          setCustomProperty(res.custom_properties[0]);
          setLoading(false);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };
*/

  const editCustomProperty = async data => {
    const customProperties = { custom_properties: Array.isArray(data) ? data : [data] };

    await customPropertyService
      .update(customProperties)
      .then(async res => {
        fetchItems();
        fetchCustomPropertyObjectMap();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editField = async data => {
    const items = { fields: Array.isArray(data) ? data : [data] };

    await fieldService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.Field);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDataset = async data => {
    const items = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.Dataset);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const editDatasetGroup = async data => {
    const items = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.DatasetGroup);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const editDatasource = async data => {
    const items = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.Datasource);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editSystem = async data => {
    const items = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.System);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editGlossary = async data => {
    const items = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.Glossary);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  /**
   * Saves an edited glossary asset. One handler serves both kinds: the form's role picker can move an
   * asset between Entity and Attribute, so which resource to call is decided from the submitted row
   * rather than from the button that opened the dialog.
   */
  const editGlossaryAsset = async data => {
    const { service, payload, isCreate } = resolveGlossaryAssetSubmit(data, 'edit');

    await (isCreate ? service.create(payload) : service.update(payload))
      .then(async res => {
        fetchAssignedAssets(Asset.Entity);
        fetchAssignedAssets(Asset.Attribute);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const editAttributeValue = async items => {
    await attributeValueService
      .update(items)
      .then(async res => {
        fetchAssignedAssets(Asset.AttributeValue);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteCustomProperty = async data => {
    await customPropertyService
      .destroy({ custom_properties: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/custom_properties`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const addCustomPropertyListValue = async data => {
    const customPropertyListValues = { custom_property_list_values: [data] };

    await customPropertyListValueService
      .create(customPropertyListValues)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchCustomPropertyListValues();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editCustomPropertyListValue = async data => {
    const customPropertyListValues = {
      custom_property_list_values: Array.isArray(data) ? data : [data],
    };

    await customPropertyListValueService
      .update(customPropertyListValues)
      .then(async res => {
        fetchCustomPropertyListValues();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteCustomPropertyListValue = async data => {
    await customPropertyListValueService
      .destroy({ custom_property_list_values: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchCustomPropertyListValues();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const removeCustomPropertyValue = async data => {
    const custom_property_values = data.map(x => ({
      custom_property_value_id: x.custom_property_value_id,
    }));

    await customPropertyValueService
      .destroy({ custom_property_values })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchAssignedAssetsCount();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const onObjectChange = object => {
    resetParams({ object: object });

    assignedAssets.length > 0 && fetchAssignedAssets(object);
    setDefaultSelectedObject({ tab: object });
    clearSelectedItems();
  };

  const onTabChange = tab => {
    clearSelectedItems();
    resetParams({ tab: tab, object: defaultSelectedObject.tab });
    //!userGroups && fetchUserGroups()

    tab === 'assigned_assets' && fetchAssignedAssets(defaultSelectedObject.tab);
  };

  const getValue = () => {
    if (!customProperty) return [];

    let name = 'Value';
    let id = 'custom_property_value';
    let type = 'string';

    let value = [];

    switch (customProperty.custom_property_type_code) {
      case 'code':
        value.push({
          id,
          name,
          type: 'code',
          className: 'code',
        });
        break;
      case 'description':
        value.push({
          id,
          name,
          type: 'description',
        });
        break;
      case 'url':
        value.push({
          id,
          name: 'URL',
          type: 'url',
          link: ':custom_property_value',
        });
        value.push({
          id: 'custom_property_secondary_value',
          name: 'Display Name',
          type,
        });
        break;
      case 'user':
        value.push({
          id: 'principal_name',
          type: 'owner',
          link: '/:principal_url',
          data: { photo: 'principal_photo' },
        });
        value.push({
          id: 'principal_name',
          name,
          type,
          link: '/:principal_url',
        });
        break;
      case 'list':
        value.push({
          id,
          name,
          type,
        });
        value.push({
          id: 'custom_property_secondary_value',
          name: 'Code',
          type,
        });
        break;
      default:
        value.push({
          id,
          name,
          type,
        });
    }

    return value;
  };

  const data = loading ? [] : customProperty;

  const editableAdmin = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const editable = editableAdmin;

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Custom Property"
          title={data.custom_property_name}
          subtitleIcon={
            <Icon
              name={data.custom_property_type_code}
              tooltip={data.custom_property_type_description}
            />
          }
          subtitleText={data.custom_property_type_name}
          description={data.custom_property_description}
          loading={loading}
          details={[
            { title: 'Last Modified', data: formatData(data.modified_timestamp, 'datetime') },
            { title: 'Created', data: formatData(data.created_timestamp, 'datetime') },
            {
              title: 'Id',
              data: data.custom_property_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          editable={editableAdmin}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editCustomProperty', [data], editCustomProperty),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteCustomProperty', [data], deleteCustomProperty),
            },
          ]}
        >
          <ObjectDetail
            label="Code"
            property="custom_property_code"
            loading={loading}
            data={data.custom_property_code}
          />

          <ObjectDetail
            label="Level"
            property="custom_property_level_name"
            loading={loading}
            data={data.custom_property_level_name}
          />

          <ObjectDetail
            label="Search Index"
            property="search_index_type_name"
            loading={loading}
            data={data.search_index_type_name}
          />

          <ObjectDetail
            label="Mapped Asset Types"
            property="custom_property_object_map"
            loading={loading}
          >
            <CheckboxList
              value={customPropertyObjectMap}
              options={CUSTOM_PROPERTY_OBJECT_MAP}
              showSelectAll={false}
              disabled={true}
            />
          </ObjectDetail>
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          disableTabsWithoutResults={false}
          showNoResultsMessage={true}
          onTabChange={onTabChange}
          defaultSelectedTab={defaultSelectedTab}
        >
          <div
            label="Assigned Assets"
            tabid="assigned_assets"
            resultCount={assignedAssetsTotalCount}
          >
            {assignedAssetsTotalCount > 0 ? (
              <Tabs
                className="buttons left"
                onTabChange={onObjectChange}
                showResultCount={false}
                disableTabsWithoutResults={true}
                defaultSelectedTab={defaultSelectedObject}
              >
                <div
                  label="Attribute Values"
                  tabid={Asset.AttributeValue}
                  resultCount={assignedAssetsCount?.attribute_value || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'attribute_value_name',
                        name: 'Name',
                        link: '/attributes/:attribute_id/:attribute_value_id',
                        tooltip: 'attribute_value_description',
                        className: 'bold',
                      },
                      { id: 'attribute_value_code', name: 'Code' },
                      {
                        id: 'attribute_role_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'attribute_role_name',
                      },
                      {
                        id: 'attribute_name',
                        name: 'Attribute',
                        link: '/attributes/:attribute_id',
                        tooltip: 'attribute_description',
                      },
                      {
                        id: 'attribute_value_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editAttributeValue',
                            items || selectedItems,
                            editAttributeValue
                          ),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="attribute_value_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'attribute_value_name',
                      'attribute_value_code',
                      'attribute_description',
                    ]}
                    showViewModes={true}
                  />
                </div>
                <div
                  label="Attributes"
                  tabid={Asset.Attribute}
                  resultCount={assignedAssetsCount?.attribute || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'attribute_role_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'attribute_role_name',
                      },
                      {
                        id: 'attribute_name',
                        name: 'Name',
                        link: '/attributes/:attribute_id',
                        tooltip: 'attribute_description',
                        className: 'bold',
                      },
                      {
                        id: 'attribute_unit_code',
                        name: 'Attribute Unit',
                        tooltip: 'attribute_unit_name',
                      },
                      {
                        id: 'attribute_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editAttribute',
                            items || selectedItems,
                            editGlossaryAsset
                          ),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="attribute_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'attribute_name',
                      'attribute_description',
                      'attribute_role_name',
                      'attribute_unit_name',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Entities"
                  tabid={Asset.Entity}
                  resultCount={assignedAssetsCount?.entity || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'entity_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id/:entity_id',
                        tooltip: 'entity_description',
                        className: 'bold',
                      },
                      {
                        id: 'entity_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'glossary_name', name: 'Glossary', link: '/glossaries/:glossary_id' },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editEntity', items || selectedItems, editGlossaryAsset),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="entity_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'entity_name',
                      'entity_description',
                      'glossary_name',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Glossaries"
                  tabid={Asset.Glossary}
                  resultCount={assignedAssetsCount?.glossary || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'glossary_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id',
                        tooltip: 'glossary_description',
                        className: 'bold',
                      },
                      {
                        id: 'glossary_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editGlossary', items || selectedItems, editGlossary),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="glossary_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'glossary_name',
                      'glossary_description',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Fields"
                  tabid={Asset.Field}
                  resultCount={assignedAssetsCount?.field || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'datatype_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datatype_fullname',
                      },
                      {
                        id: 'field_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
                        tooltip: 'field_description',
                        className: 'bold',
                      },
                      {
                        id: 'field_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        // dataset_path holds the formatDatasetPath() output (its segments link
                        // themselves); sort still keys off dataset_name.
                        id: 'dataset_path',
                        name: 'Dataset',
                        sortField: 'dataset_name',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDataset', items || selectedItems, editDataset),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="field_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'datatype_fullname',
                      'field_name',
                      'dataset_name',
                      'dataset_group_name',
                      'datasource_name',
                      'system_name',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasets"
                  tabid={Asset.Dataset}
                  resultCount={assignedAssetsCount?.dataset || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'dataset_type_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'dataset_type_name',
                      },
                      {
                        // dataset_path holds the formatDatasetPath() output (its segments link
                        // themselves); sort still keys off dataset_name.
                        id: 'dataset_path',
                        name: 'Name',
                        sortField: 'dataset_name',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDataset', items || selectedItems, editDataset),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="dataset_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'dataset_name',
                      'dataset_description',
                      'dataset_type_name',
                      'dataset_group_name',
                      'datasource_name',
                      'system_name',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Dataset Groups"
                  tabid={Asset.DatasetGroup}
                  resultCount={assignedAssetsCount?.dataset_group || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'dataset_group_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                        tooltip: 'dataset_group_description',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editDatasetGroup',
                            items || selectedItems,
                            editDatasetGroup
                          ),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="dataset_group_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'dataset_group_name',
                      'dataset_description',
                      'datasource_name',
                      'system_name',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasources"
                  tabid={Asset.Datasource}
                  resultCount={assignedAssetsCount?.datasource || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id',
                        tooltip: 'datasource_description',
                        className: 'bold',
                      },
                      {
                        id: 'datasource_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'system_name', name: 'System', link: '/systems/:system_id' },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDatasource', items || selectedItems, editDatasource),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="datasource_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'datasource_name',
                      'datasource_description',
                      'datasource_type_category',
                      'datasource_type_name',
                      'system_name',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Systems"
                  tabid={Asset.System}
                  resultCount={assignedAssetsCount?.system || 0}
                >
                  <DataTable
                    columns={[
                      ...getValue(),
                      {
                        id: 'system_name',
                        name: 'Name',
                        link: '/systems/:system_id',
                        tooltip: 'system_description',
                        className: 'bold',
                      },
                      { id: 'system_code', name: 'Code' },
                      {
                        id: 'system_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editSystem', items || selectedItems, editSystem),
                      },
                      {
                        label: 'Remove',
                        action: 'remove',
                        onClick: items =>
                          showModalDialog(
                            'removeCustomPropertyValue',
                            items || selectedItems,
                            removeCustomPropertyValue
                          ),
                      },
                    ]}
                    data={assignedAssets}
                    idColumn="system_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'custom_property_value',
                      'system_name',
                      'system_code',
                      'system_description',
                    ]}
                    showViewModes={true}
                  />
                </div>
              </Tabs>
            ) : (
              !loadingTable && <div className="no-result-text">No assigned assets</div>
            )}
          </div>
          {customProperty?.custom_property_type_code === 'list' && (
            <div
              label="Values"
              tabid="values"
              resultCount={
                loading || customPropertyListValues.length === 0
                  ? undefined
                  : customPropertyListValues.length
              }
            >
              <DataTable
                columns={[
                  {
                    id: 'custom_property_list_value_name',
                    name: 'Name',
                    className: 'bold',
                  },
                  {
                    id: 'custom_property_list_value_code',
                    name: 'Code',
                  },
                  {
                    id: 'custom_property_list_value_description',
                    name: 'Description',
                    type: 'description',
                    viewMode: 'descriptions',
                  },
                  {
                    id: 'modified_timestamp',
                    name: 'Modified',
                    type: 'datetime',
                  },
                ]}
                buttons={[
                  {
                    label: '+ Add',
                    action: 'add',
                    mainButton: true,
                    onClick: items => {
                      showSideDialog(
                        'addCustomPropertyListValue',
                        items || selectedItems,
                        addCustomPropertyListValue
                      );
                    },
                  },
                  {
                    label: 'Edit',
                    action: 'edit',
                    onClick: items => {
                      showSideDialog(
                        'editCustomPropertyListValue',
                        items || selectedItems,
                        editCustomPropertyListValue
                      );
                    },
                  },
                  {
                    label: 'Delete',
                    action: 'delete',
                    onClick: items =>
                      showModalDialog(
                        'deleteCustomPropertyListValue',
                        items || selectedItems,
                        deleteCustomPropertyListValue
                      ),
                  },
                ]}
                data={customPropertyListValues}
                idColumn="custom_property_list_value_id"
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                loading={loading}
                editable={editable}
                filterable={true}
                filterObjects={[
                  'custom_property_list_value_name',
                  'custom_property_list_value_code',
                  'custom_property_list_value_description',
                ]}
                showViewModes={true}
              />
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
};
